import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

// Import estático de la imagen para evitar problemas de ruta
const tamaraImg = '/images/tamara.jpg';

// Calendly Integration Component
const CalendlyButton = ({ 
  text = "Reserva tu sesión", 
  variant = "primary", 
  size = "medium",
  className = "",
  showMicrocopy = true,
  icon = "📅"
}) => {
  const { t } = useTranslations();
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";
  
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 text-center";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg",
    secondary: "border-2 border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300",
    outline: "border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40",
    accent: "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg"
  };
  
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <div className="text-center">
      <a
        href={calendlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        aria-label="Reservar sesión gratuita de 30 minutos con Tamara Navarrete"
      >
        <span className="mr-2">{icon}</span>
        {text}
      </a>
      {showMicrocopy && (
        <p className="text-sm text-gray-400 mt-2">
          {t('advisory.freeSession.text')}
        </p>
      )}
    </div>
  );
};

// Stripe Direct Link Component (para enlaces directos de Stripe)
const StripeDirectButton = ({ 
  stripeUrl, 
  text = "Contratar ahora", 
  className = "",
  size = "medium" 
}) => {
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };

  const buttonClasses = `w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg ${sizes[size]} ${className}`;

  return (
    <a 
      href={stripeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={buttonClasses}
    >
      {text}
    </a>
  );
};

// Stripe Checkout Component
const StripeButton = ({ 
  packageId, 
  text = "Contratar ahora", 
  className = "",
  size = "medium" 
}) => {
  const { t, language } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: originUrl,
          language: language, // Add language parameter for LIVE checkout
          metadata: {
            source: 'asesoria_page',
            package_name: packageId,
            user_language: language
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError(t('pricing.errors.paymentGeneral'));
    } finally {
      setIsLoading(false);
    }
  };

  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };

  const buttonClasses = `w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg ${sizes[size]} ${className}`;

  if (error) {
    return (
      <div className="text-center">
        <p className="text-red-400 text-sm mb-2">{error}</p>
        <button onClick={handleCheckout} className={buttonClasses} disabled={isLoading}>
          {isLoading ? 'Procesando...' : 'Reintentar'}
        </button>
      </div>
    );
  }

  return (
    <button 
      onClick={handleCheckout} 
      className={buttonClasses}
      disabled={isLoading}
    >
      {isLoading ? 'Procesando...' : text}
    </button>
  );
};

const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.home')}</Link>
            <Link to="/demo" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.demo')}</Link>
            <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.pricing')}</Link>
            <Link to="/asesoria" className="text-purple-400 hover:text-purple-300 transition-colors text-sm lg:text-base font-semibold">{t('navigation.consulting')}</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.contact')}</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
              aria-label="Abrir menú"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.home')}</Link>
              <Link to="/demo" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.demo')}</Link>
              <Link to="/pricing" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.pricing')}</Link>
              <Link to="/asesoria" className="block text-purple-400 hover:text-purple-300 py-2 transition-colors font-semibold" onClick={closeMobileMenu}>{t('navigation.consulting')}</Link>
              <Link to="/contacto" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.contact')}</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const AsesoriaPageNew = () => {
  const { t } = useTranslations();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqData = [
    {
      question: t('advisory.faq.questions.q1.question'),
      answer: t('advisory.faq.questions.q1.answer')
    },
    {
      question: t('advisory.faq.questions.q2.question'),
      answer: t('advisory.faq.questions.q2.answer')
    },
    {
      question: t('advisory.faq.questions.q3.question'),
      answer: t('advisory.faq.questions.q3.answer')
    },
    {
      question: t('advisory.faq.questions.q4.question'),
      answer: t('advisory.faq.questions.q4.answer')
    },
    {
      question: t('advisory.faq.questions.q5.question'),
      answer: t('advisory.faq.questions.q5.answer')
    },
    {
      question: t('advisory.faq.questions.q6.question'),
      answer: t('advisory.faq.questions.q6.answer')
    }
  ];

  const services = [
    // 1. Auditoría Express - €400 (más accesible)
    {
      id: "audit_express",
      icon: "🔎",
      title: "Auditoría Express",
      subtitle: "(Marcas Personales)",
      description: "Una revisión rápida y enfocada para marcas personales que necesitan claridad inmediata sin esperar una auditoría completa.",
      price: "€400",
      duration: "5 días",
      type: "stripe_direct",
      stripeUrl: "https://buy.stripe.com/test_dRm3cx4Zt8Ms7akbyidwc03",
      productId: "prod_SsqpDT9KClXSVa",
      features: [
        "Evaluación de tu posicionamiento actual como marca personal",
        "Análisis de tu presencia digital (redes sociales, web y huella online)",
        "Informe breve con 3 puntos clave a reforzar y 3 acciones inmediatas",
        "Recomendaciones prácticas adaptadas a tu situación actual"
      ],
      highlight: "Ideal si quieres una hoja de ruta clara y accionable para empezar a diferenciarte"
    },
    // 2. Sesiones Individuales - desde €150 (entrada flexible)
    {
      id: "sesiones_individuales",
      icon: "🎤",
      title: t('advisory.services.individual.title'),
      description: t('advisory.services.individual.description'),
      price: t('advisory.services.individual.price'),
      duration: t('advisory.services.individual.duration'),
      type: "stripe",
      packageId: "sesiones_individuales", // Maps to LIVE price_1SBvjy2NNiHsDPQ5xmMXm2s9
      features: [
        t('advisory.services.individual.feature1'),
        t('advisory.services.individual.feature2'),
        t('advisory.services.individual.feature3'), 
        t('advisory.services.individual.feature4')
      ]
    },
    // 3. Estrategia de Crecimiento - €800 (estratégica)
    {
      id: "estrategia_crecimiento", 
      icon: "📈",
      title: t('advisory.services.growth.title'),
      description: t('advisory.services.growth.description'),
      price: t('advisory.services.growth.price'),
      duration: t('advisory.services.growth.duration'), 
      type: "stripe",
      packageId: "estrategia_crecimiento", // Maps to LIVE price_1SBvk12NNiHsDPQ5sxdPwkML
      features: [
        t('advisory.services.growth.feature1'),
        t('advisory.services.growth.feature2'), 
        t('advisory.services.growth.feature3'),
        t('advisory.services.growth.feature4')
      ]
    },
    // 4. Auditoría de Marca - €1,200 (premium)
    {
      id: "auditoria_marca",
      icon: "🔍",
      title: t('advisory.services.audit.title'),
      description: t('advisory.services.audit.description'),
      price: t('advisory.services.audit.price'),
      duration: t('advisory.services.audit.duration'),
      type: "stripe",
      packageId: "auditoria_marca", // Maps to LIVE price_1SBvk32NNiHsDPQ5IXuuAFUa
      features: [
        t('advisory.services.audit.feature1'),
        t('advisory.services.audit.feature2'), 
        t('advisory.services.audit.feature3'),
        t('advisory.services.audit.feature4')
      ]
    }
  ];

  // Servicios visibles (sin Auditoría Express)
  const visibleServices = services.filter(service => service.id !== "audit_express");

  // Agregar estilos para ocultar scrollbar
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const testimonials = [
    {
      name: t('advisory.testimonials.maria.name'),
      position: t('advisory.testimonials.maria.role'),
      company: t('advisory.testimonials.maria.company'),
      testimonial: t('advisory.testimonials.maria.quote'),
      results: t('advisory.testimonials.maria.result'),
      photo: "👩‍💼"
    },
    {
      name: t('advisory.testimonials.carlos.name'),
      position: t('advisory.testimonials.carlos.role'),
      company: t('advisory.testimonials.carlos.company'),
      testimonial: t('advisory.testimonials.carlos.quote'),
      results: t('advisory.testimonials.carlos.result'),
      photo: "👨‍🚀"
    },
    {
      name: t('advisory.testimonials.laura.name'),  
      position: t('advisory.testimonials.laura.role'),
      company: t('advisory.testimonials.laura.company'),
      testimonial: t('advisory.testimonials.laura.quote'),
      results: t('advisory.testimonials.laura.result'),
      photo: "🎯"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* BLOQUE 1: Presentación de Tamara con logros destacados */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            {t('home.calculator.backHome')}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Información Personal con Foto */}
            <div className="space-y-8">
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
                <div className="text-center mb-8">
                  {/* Foto de Tamara - Desktop: integrada en el texto, Mobile: centrada arriba */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:text-left lg:space-x-6 mb-6">
                    {/* Foto responsive - simplificada sin picture element */}
                    <div className="mx-auto lg:mx-0 mb-6 lg:mb-0 flex-shrink-0">
                      <img
                        src={tamaraImg}
                        alt="Retrato de Tamara Navarrete Navas, CMO y consultora en marketing humanizado y estrategia de marca"
                        loading="lazy"
                        width={128}
                        height={128}
                        className="w-32 h-32 object-cover rounded-full ring-4 ring-purple-500/50 shadow-2xl hover:ring-purple-400/60 transition-all duration-300"
                        style={{ objectFit: 'cover' }}
                        onError={(e) => {
                          console.error('Error loading Tamara image:', e);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    {/* Información personal */}
                    <div className="text-center lg:text-left">
                      <h1 className="text-5xl font-bold text-white mb-4">{t('advisory.hero.title')}</h1>
                      <p className="text-xl text-purple-300 mb-6">
                        {t('advisory.hero.subtitle')}
                      </p>
                      <p className="text-lg text-gray-300 mb-8">
                        {t('advisory.hero.description')}
                      </p>
                      <div className="flex flex-col lg:flex-row lg:space-x-4 space-y-2 lg:space-y-0 text-sm text-gray-300 justify-center lg:justify-start">
                        <span className="flex items-center justify-center lg:justify-start">
                          <span className="mr-2">📍</span>{t('advisory.hero.location')}
                        </span>
                        <span className="flex items-center justify-center lg:justify-start">
                          <span className="mr-2">💼</span>{t('advisory.hero.experience')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-xl p-6 border border-purple-500/20">
                    <h3 className="text-xl font-bold text-white mb-4">{t('advisory.session.title')}</h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <span className="text-purple-400 text-lg">✓</span>
                        <span className="text-gray-300">{t('advisory.session.point1')}</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <span className="text-purple-400 text-lg">✓</span>
                        <span className="text-gray-300">{t('advisory.session.point2')}</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <span className="text-purple-400 text-lg">✓</span>
                        <span className="text-gray-300">{t('advisory.session.point3')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Logros Destacados */}
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8">
                <h2 className="text-2xl font-bold text-white mb-8 text-center">{t('advisory.achievements.title')}</h2>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400 mb-2">500+</div>
                    <div className="text-sm text-gray-300">{t('advisory.achievements.talks')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400 mb-2">15</div>
                    <div className="text-sm text-gray-300">{t('advisory.achievements.countries')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400 mb-2">1K+</div>
                    <div className="text-sm text-gray-300">{t('advisory.achievements.references')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400 mb-2">7.5K+</div>
                    <div className="text-sm text-gray-300">{t('advisory.achievements.followers')}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-green-400 mt-1">🏆</span>
                    <div>
                      <div className="font-semibold text-white">{t('advisory.experience.cmo.title')}</div>
                      <div className="text-sm text-gray-400">{t('advisory.experience.cmo.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-green-400 mt-1">📈</span>
                    <div>
                      <div className="font-semibold text-white">{t('advisory.experience.emburse.title')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-green-400 mt-1">🎯</span>
                    <div>
                      <div className="font-semibold text-white">{t('advisory.experience.expert.title')}</div>
                      <div className="text-sm text-gray-400">{t('advisory.experience.expert.description')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="text-center">
                <CalendlyButton 
                  text={t('advisory.freeSession.cta')}
                  variant="primary"
                  size="large"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 2: Servicios Ofrecidos */}
      <section id="servicios" className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('advisory.services.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t('advisory.services.subtitle')}
            </p>
            
            {/* Bloque Enfoque Personalizado */}
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl border border-blue-500/30 p-8 max-w-4xl mx-auto mb-12">
              <h3 className="text-2xl font-bold text-white mb-6">{t('advisory.methodology.title')}</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('advisory.methodology.subtitle')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl mb-3">🎯</div>
                  <h4 className="font-bold text-white mb-2">{t('advisory.methodology.currentPhase.title')}</h4>
                  <p className="text-gray-300 text-sm">{t('advisory.methodology.currentPhase.description')}</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">🔧</div>
                  <h4 className="font-bold text-white mb-2">{t('advisory.methodology.specificChallenges.title')}</h4>
                  <p className="text-gray-300 text-sm">{t('advisory.methodology.specificChallenges.description')}</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">📈</div>
                  <h4 className="font-bold text-white mb-2">{t('advisory.methodology.realObjectives.title')}</h4>
                  <p className="text-gray-300 text-sm">{t('advisory.methodology.realObjectives.description')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contenedor centrado de las tarjetas */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Filtrar servicios para excluir Auditoría Express */}
            {(() => {
              const visibleServices = services.filter(service => service.id !== "audit_express");
              
              return (
                <>
                  {/* Contenedor centrado de todas las tarjetas */}
                  <div className="flex justify-center">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl">
                      {visibleServices.map((service, index) => (
                        <div
                          key={index}
                          className="group relative w-full max-w-[320px] mx-auto"
                          tabIndex="0"
                        >
                          {/* Card Container - más compacta y limpia */}
                          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/90 backdrop-blur-sm border border-slate-700/60 rounded-xl p-6 h-full flex flex-col justify-between transform transition-all duration-300 group-hover:scale-[1.02] group-hover:border-purple-400/60 group-hover:shadow-lg group-hover:shadow-purple-500/20 min-h-[480px]">
                            
                            {/* Contenido superior */}
                            <div className="flex-grow space-y-4">
                              {/* Service Icon */}
                              <div className="flex justify-center">
                                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center text-2xl transform transition-transform duration-300 group-hover:scale-105">
                                  {service.icon}
                                </div>
                              </div>
                              
                              {/* Service Header */}
                              <div className="text-center space-y-3">
                                <h4 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors leading-tight">
                                  {service.title}
                                  {service.subtitle && (
                                    <span className="block text-base font-normal text-purple-400 mt-1">
                                      {service.subtitle}
                                    </span>
                                  )}
                                </h4>
                                
                                <p className="text-gray-300 text-sm leading-relaxed">
                                  {service.description}
                                </p>
                                
                                {/* Highlight text for Auditoría Express */}
                                {service.highlight && (
                                  <p className="text-green-400 text-xs font-medium italic">
                                    👉 {service.highlight}
                                  </p>
                                )}
                                
                                {/* Price & Duration */}
                                <div className="flex justify-center items-center space-x-3">
                                  <span className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                                    {service.price}
                                  </span>
                                  <span className="text-gray-400 text-sm">
                                    • {service.duration}
                                  </span>
                                </div>
                              </div>
                                
                              {/* Features List - más compacta */}
                              <div className="space-y-2">
                                {service.features.map((feature, idx) => (
                                  <div key={idx} className="flex items-start space-x-2">
                                    <div className="w-1.5 h-1.5 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                                    <span className="text-gray-300 text-xs leading-relaxed">{feature}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* CTAs - centrados */}
                            <div className="flex flex-col items-center space-y-3 mt-6">
                              {/* Primary CTA */}
                              <div className="w-full flex justify-center">
                                {service.type === 'stripe_direct' ? (
                                  <StripeDirectButton 
                                    stripeUrl={service.stripeUrl}
                                    text={t('advisory.services.individual.cta')}
                                    size="medium"
                                    className="w-full max-w-[240px] text-center"
                                  />
                                ) : service.type === 'stripe' ? (
                                  <StripeButton 
                                    packageId={service.packageId}
                                    text={t('advisory.services.individual.cta')}
                                    size="medium"
                                    className="w-full max-w-[240px] text-center"
                                  />
                                ) : (
                                  <CalendlyButton 
                                    text="Reservar sesión"
                                    variant="primary"
                                    size="medium"
                                    showMicrocopy={false}
                                    icon="🎤"
                                    className="w-full max-w-[240px] text-center"
                                  />
                                )}
                              </div>
                              
                              {/* Secondary CTA centrado */}
                              <CalendlyButton 
                                text={t('advisory.services.individual.doubt')}
                                variant="secondary"
                                size="small"
                                showMicrocopy={false}
                                icon="💬"
                                className="text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Mensaje adicional */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl border border-blue-500/30 p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-4">{t('advisory.differentApproach.title')}</h3>
              <p className="text-gray-300 mb-6">
                {t('advisory.differentApproach.description')}
              </p>
              <Link
                to="/contacto"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
              >
                <span className="mr-2">💬</span>
                {t('advisory.differentApproach.bookSession')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 3: Testimonios de Clientes */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('advisory.testimonials.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('advisory.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-800/50 to-purple-900/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 hover:border-purple-500/40 transition-all duration-300 
                           grid grid-rows-[auto,1fr,auto] min-h-[400px]"
              >
                {/* Bloque 1: Información del cliente (auto) */}
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-3xl">
                    {testimonial.photo}
                  </div>
                  <h4 className="text-xl font-bold text-white">{testimonial.name}</h4>
                  <p className="text-purple-300 text-sm">{testimonial.position}</p>
                  <p className="text-gray-400 text-xs">{testimonial.company}</p>
                </div>

                {/* Bloque 2: Testimonio (1fr - se expande para igualar altura) */}
                <div className="flex items-center">
                  <blockquote className="text-gray-300 text-center italic leading-relaxed">
                    "{testimonial.testimonial}"
                  </blockquote>
                </div>

                {/* Bloque 3: Botón de resultados (auto - siempre en la misma posición) */}
                <div className="text-center mt-6">
                  <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-full px-4 py-2 inline-block">
                    <span className="text-green-400 font-bold text-sm">{testimonial.results}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action Final */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-white mb-4">{t('advisory.finalCta.title')}</h3>
              <p className="text-xl text-gray-300 mb-8">
                {t('advisory.finalCta.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <CalendlyButton 
                  text={t('advisory.finalCta.bookSession')}
                  variant="primary"
                  size="large"
                  showMicrocopy={false}
                />
                <button
                  onClick={() => {
                    const servicesSection = document.getElementById('servicios');
                    if (servicesSection) {
                      servicesSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  className="border-2 border-pink-500 text-pink-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-pink-500 hover:text-white transition-all duration-300"
                >
                  {t('advisory.finalCta.viewServices')}
                </button>
              </div>
              
              <p className="text-sm text-gray-400 mt-4">
                {t('advisory.finalCta.benefits')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sección FAQ */}
      <section className="py-16 bg-slate-800/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              {t('advisory.faq.title')}
            </h3>
            <p className="text-xl text-gray-300">
              {t('advisory.commonQuestions.title')}
            </p>
          </div>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden hover:border-purple-400/50 transition-all duration-300">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-700/30 transition-colors duration-200"
                >
                  <h4 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h4>
                  <div className={`text-purple-400 text-2xl transform transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                    ↓
                  </div>
                </button>
                
                {openFAQ === index && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-700/50 pt-4">
                      <p className="text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* CTA Final */}
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-purple-800/30 to-blue-800/30 rounded-2xl border border-purple-400/30 p-8">
              <h4 className="text-2xl font-bold text-white mb-4">
                {t('advisory.moreQuestions.title')}
              </h4>
              <p className="text-gray-300 mb-6">
                {t('advisory.moreQuestions.subtitle')}
              </p>
              <CalendlyButton 
                text={t('advisory.moreQuestions.bookSession')}
                variant="accent"
                size="large"
                icon="💬"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AsesoriaPageNew;